import './ModalEditWidget.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import {
  JSX,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { MdAdd, MdOutlineReplay, MdSave } from 'react-icons/md';
import { RiAlertFill } from 'react-icons/ri';
import * as uuid from 'uuid';
import { truncate } from '../../utils/text-utils';
import { AlertDialog } from '../AlertDialog';
import { Button } from '../Button/Button';
import { Appearance, Background, Intent, Placement, Size } from '../designConstants';
import { InteractiveDiv } from '../InteractiveEl';
import { NumberFormatter } from '../NumberFormatter/NumberFormatter';
import { PendingChangesPanel } from '../PendingChanges';
import { RowType } from '../PendingChanges/pending-changes-utils';
import { BPMTable } from '../StickyTable/BPMTable';
import { TableDensity } from '../StickyTable/table-density';
import { TableEmptyState } from '../TableEmptyState';
import { WidgetContainer } from '../WidgetContainer';
import {
  CheckCanDeleteFn,
  CheckIsValidFn,
  CustomFooterButton,
  CustomModalEditWidgetHeader,
  ModalEditWidgetHandle,
  PRETTY_DATE_FORMAT,
  ROW_HEIGHT,
  SaveChangesInput,
  SelectorOptions,
  TableRow
} from './modal-edit-constants';
import { findBrokenFlexFillAncestor, findRowIdProblem } from './modal-edit-utils';
import { ModalEditor } from './ModalEditor';

const NoRowsRenderer = () => <TableEmptyState message='No rows to show' />;

const RICH_TEXT_CELL_MAX_LENGTH = 80;

const renderForType = (type: string, value: string | boolean | JSX.Element, _isMulti: boolean) => {
  switch (type) {
    case 'percent':
      return <NumberFormatter value={parseFloat(value as string)} type='%' />;
    case 'currency':
      return <NumberFormatter value={parseFloat(value as string)} type='$' />;
    case 'decimal':
    case 'real':
      return <NumberFormatter value={parseFloat(value as string)} type='#' />;
    case 'timestamp':
      return Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(value as string));
    case 'checkbox':
      return <Checkbox checked={value as boolean} disabled readOnly />;
    case 'file':
      return value || 'No file';
    case 'richText': {
      const singleLine = (typeof value === 'string' ? value : '').replace(/\s+/g, ' ').trim();
      return truncate(singleLine, RICH_TEXT_CELL_MAX_LENGTH);
    }
    default:
      return value;
  }
};

interface ModalEditWidgetProps<T extends object> {
  // Data state management
  initialTableData: T[];
  headers: CustomModalEditWidgetHeader[]; // Column definitions: field names, labels, types, widths, and renderers
  checkIsValid?: CheckIsValidFn<T>;
  checkCanDelete?: CheckCanDeleteFn<T>;
  onSaveModal?: (input: SaveChangesInput<T>) => void | Promise<void>;
  onDiscard?: () => void;
  // When true, the row editor's Save/Delete commits that single row through `onSaveModal` immediately
  // instead of staging it into pending changes for a batched footer save. Default false (batch mode).
  saveOnEdit?: boolean;
  readOnly?: boolean; // readOnly takes precedent over enableAdd / enableDelete
  enableAdd?: boolean;
  enableDelete?: boolean;
  defaultNewRow?: Partial<T>; // Initial field values pre-populated when adding a new row

  // FilterBar options
  filterBar?: boolean;
  defaultAdvancedFilter?: boolean; // Whether the filter bar starts in advanced mode
  dynamicTokens?: string[]; // Token keys available in the advanced filter bar
  extraFooterActions?: ReactNode; // Additional buttons rendered in the row editor footer
  headerRightActions?: ReactNode; // Custom actions rendered in the widget header's right slot, before the Add button

  // Custom rendering
  addButtonText?: string; // Label for the Add button (defaults to "Add")
  addButtonLoading?: boolean; // When true, the Add button shows a spinner and is disabled (e.g. while options load)
  deleteButtonText?: string; // Label for the Add button (defaults to "Add")
  saveButtonText?: string;
  footerText?: string; // Informational text shown in the row editor footer
  footerMetadata?: (data: Partial<T>) => ReactNode; // Small readonly info (e.g. last-modified) shown alongside the footer's action buttons
  name?: string | JSX.Element; // Heading displayed above the table in the widget header
  modalTitle?: string | JSX.Element; // Title shown in the row editor modal (falls back to `name` if omitted)
  autoHeight?: boolean; // Grow with table rows instead of creating a nested vertical scrollbar
  isLoading?: boolean; // Keeps the header, filter bar and column headers mounted and shows skeleton rows; disables Add
  density?: TableDensity;
  /** @deprecated Pass `density`. */
  rowHeight?: number;
  rowHeightGetter?: (row: TableRow<T>) => number; // Per-row height resolved by row identity (correct under sort/filter); takes precedence over the density row height
  emptyContent?: ReactNode; // Rendered in the table body when there are no rows (replaces the default "No rows to show"). When set, the widget stays mounted and the header Add button is hidden while the loaded table is empty so this content can own the CTA.
  pagination?: boolean; // Enables pagination controls below the table
  selectorOptions: SelectorOptions; // Options for select/multi-select field types, keyed by field name
  customModalFooterButtons?: CustomFooterButton<T>[];
  checkShowModalFooter?: (elem: Partial<T>) => boolean;
  // When provided, replaces the built-in ModalEditor — both props required together to prevent half-wired state
  customEditor?: {
    onRowClick: (row: TableRow<T>) => void;
    onAddClick: () => void;
  };

  // TODO: Support reordering and selection actions with new onSaveModal signature / state management
  // enableActionColumn?: boolean;
  // actionColumnWidth?: number;
  // draggableRows?: boolean;
  // showReorderActions?: boolean;
  // onMoveRow?: (dragIndex: number, hoverIndex: number) => void;
  // totals?: Partial<TableDataType>;
  // totalsRenderer?: CellRenderer<Element | number | string | undefined>;

  /** Stable key per row of `initialTableData` (e.g. `row => String(row.id)`). Must return a unique, non-empty value; defaults to array index. */
  getRowId?: (row: T) => string;
  dataTestId?: string;
  className: string;
}

export const ModalEditWidget = <T extends object>({
  ref,
  initialTableData,
  headers,
  checkIsValid = null,
  checkCanDelete,
  onSaveModal,
  onDiscard,
  saveOnEdit = false,
  readOnly = false,
  enableAdd = true,
  enableDelete = true,
  defaultNewRow = {},
  filterBar = true,
  defaultAdvancedFilter = false,
  dynamicTokens = [],
  extraFooterActions,
  headerRightActions,
  addButtonText = 'Add',
  addButtonLoading = false,
  deleteButtonText,
  saveButtonText,
  footerText,
  footerMetadata,
  name = '',
  modalTitle,
  autoHeight = false,
  isLoading = false,
  density = TableDensity.COMPACT,
  rowHeight,
  rowHeightGetter,
  emptyContent,
  pagination = false,
  selectorOptions,
  customModalFooterButtons,
  checkShowModalFooter,
  customEditor,
  dataTestId,
  getRowId,
  className
}: ModalEditWidgetProps<T> & { ref?: Ref<ModalEditWidgetHandle<T>> }): JSX.Element => {
  if (!readOnly && !onSaveModal && !customEditor) {
    throw new Error('ModalEditWidget is not readOnly, but does not have save function');
  }

  const [filterBarContainer, setFilterBarContainer] = useState<HTMLDivElement | null>(null);
  const tableDataMap: Record<string, T> = useMemo(
    () =>
      Object.fromEntries(
        initialTableData.map((data, index) => [getRowId ? getRowId(data) : index, data])
      ),
    [initialTableData, getRowId]
  );

  const [modifiedRows, setModifiedRows] = useState<Record<string, T>>({});
  const [addedRows, setAddedRows] = useState<Record<string, T>>({});
  const [deletedRows, setDeletedRows] = useState<Record<string, T>>({});
  // Key of the row in tableDataMap currently being edited
  const [editingData, setEditingData] = useState<TableRow<Partial<T>> | null>(null);
  const [editingType, setEditingType] = useState<'adding' | 'editing' | null>(null);
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const openAddModal = useCallback(() => {
    setEditingData({ ...defaultNewRow, rowKey: uuid.v4() });
    setEditingType('adding');
  }, [defaultNewRow]);

  useImperativeHandle(
    ref,
    () => ({
      openAddModal,
      trackModify: (rowKey, row) => {
        if (addedRows[rowKey]) {
          setAddedRows(prev => ({ ...prev, [rowKey]: row }));
          return;
        }
        setModifiedRows(prev => ({ ...prev, [rowKey]: row }));
      },
      trackAdd: row => {
        const key = uuid.v4();
        setAddedRows(prev => ({ ...prev, [key]: row }));
        return key;
      },
      trackDelete: rowKey => {
        if (addedRows[rowKey]) {
          setAddedRows(prev => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
          });
          return;
        }
        const rowData = modifiedRows[rowKey] ?? tableDataMap[rowKey];
        if (!rowData) return;
        setDeletedRows(prev => ({ ...prev, [rowKey]: rowData }));
        setModifiedRows(prev => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
      }
    }),
    [addedRows, modifiedRows, tableDataMap, openAddModal]
  );

  const tableData: TableRow<T>[] = useMemo(() => {
    const map = { ...tableDataMap };

    for (const [key, value] of Object.entries(modifiedRows)) {
      map[key] = value;
    }

    for (const [key, value] of Object.entries(addedRows)) {
      map[key] = value;
    }

    Object.keys(deletedRows).forEach(key => {
      delete map[key];
    });

    return Object.entries(map).map(([key, value]) => ({
      ...value,
      rowKey: key
    }));
  }, [addedRows, deletedRows, modifiedRows, tableDataMap]);

  const renderEmptyContent = useCallback(() => <>{emptyContent}</>, [emptyContent]);
  const showsEmptyContent = emptyContent !== undefined && !isLoading && tableData.length === 0;

  const tableHeaders: CustomModalEditWidgetHeader[] = useMemo(
    () =>
      [
        ...headers.map(header => ({
          ...header,
          width: header.width ?? 120,
          minFlexWidth: header.minFlexWidth ?? 200,
          name: header.field,
          renderer: (data: TableRow<T>) => {
            const { renderer, field = '', type = '', isMulti = false, uneditable } = header;

            const { onClick } = (() => {
              if (readOnly || uneditable) {
                return {};
              }

              if (customEditor) {
                return {
                  onClick: () => customEditor.onRowClick(data)
                };
              }

              const editingTypeToSet = tableDataMap[data.rowKey] ? 'editing' : 'adding';

              return {
                onClick: () => {
                  setEditingData(data);
                  setEditingType(editingTypeToSet);
                }
              };
            })();

            if (renderer) {
              return (
                <InteractiveDiv
                  className='modal-edit-table-cell modal-edit-table-custom-renderer-cell'
                  data-testid='modal-edit-table-cell'
                  onClick={onClick}
                >
                  {renderer(data, header.field) as ReactNode}
                </InteractiveDiv>
              );
            }

            let displayValue: boolean | string | JSX.Element = <div className='empty-cell'>-</div>;

            if (data[field] != null && !isMulti) {
              displayValue = renderForType(type, data[field], isMulti);
            } else if (data[field] != null && isMulti && data[field].length === 1) {
              displayValue = <span>[{data[field]}]</span>;
            } else if (data[field] != null && isMulti && data[field].length > 1) {
              displayValue = (
                <OverlayTrigger
                  placement={Placement.BOTTOM}
                  overlay={<Tooltip id={uuid.v4()}>{data[field].join(', ')}</Tooltip>}
                >
                  <span>{data[field].length} selected</span>
                </OverlayTrigger>
              );
            }

            return (
              <InteractiveDiv
                className='modal-edit-table-cell'
                data-testid='modal-edit-table-cell'
                onClick={onClick}
              >
                {displayValue}
              </InteractiveDiv>
            );
          }
        }))
      ].filter(elem => elem != null),
    [customEditor, headers, readOnly, tableDataMap]
  );

  const pendingChangeCount = useMemo(
    () =>
      Object.keys(modifiedRows).length +
      Object.keys(addedRows).length +
      Object.keys(deletedRows).length,
    [addedRows, deletedRows, modifiedRows]
  );
  const hasPendingChanges = pendingChangeCount > 0;

  const { seePendingChangesCopy, saveChangesCopy } = useMemo(
    () => ({
      seePendingChangesCopy:
        pendingChangeCount > 1
          ? `See ${pendingChangeCount} Pending Changes`
          : 'See 1 Pending Change',
      saveChangesCopy:
        pendingChangeCount > 1 ? `Save ${pendingChangeCount} Changes` : 'Save 1 Change'
    }),
    [pendingChangeCount]
  );

  const showFooter = !readOnly && onSaveModal && (hasPendingChanges || extraFooterActions);

  const clearChanges = useCallback(() => {
    setModifiedRows({});
    setAddedRows({});
    setDeletedRows({});
  }, []);

  // saveOnEdit: commit a single row's change straight through onSaveModal (no pending stage).
  const commitSingle = useCallback(
    (change: { added?: T; modified?: T; deleted?: T }) => {
      onSaveModal?.({
        addedData: change.added ? [change.added] : [],
        modifiedData: change.modified ? [change.modified] : [],
        deletedData: change.deleted ? [change.deleted] : [],
        cleanup: () => {}
      });
    },
    [onSaveModal]
  );

  const [isSaving, setIsSaving] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!import.meta.env.DEV || !getRowId) return;

    const problem = findRowIdProblem(initialTableData, getRowId);
    if (problem) {
      console.warn(
        `ModalEditWidget "${name}": getRowId returned ${problem} -- rows sharing a key collapse into one another and disappear from the table. getRowId must return a unique, defined value for every row.`
      );
    }
  }, [initialTableData, getRowId, name]);

  useEffect(() => {
    if (!import.meta.env.DEV || !containerRef.current) return;

    const brokenAncestor = findBrokenFlexFillAncestor(containerRef.current);
    if (brokenAncestor) {
      console.warn(
        `ModalEditWidget "${name}": ancestor <${brokenAncestor.tagName.toLowerCase()} class="${brokenAncestor.className}"> grows to fill its flex parent but has no "min-height: 0", so its default content-based minimum size can let this widget grow past the viewport instead of scrolling internally. Add "min-height: 0" (or "@include sassHelpers.flexFill;") on that element -- see "ModalEditWidget min-height:0 chain" in docs/MIGRATION_GUIDE.md.`
      );
    }
  }, [name]);

  return (
    <>
      <WidgetContainer
        containerRef={containerRef}
        className={cn('modal-edit-widget', className)}
        dataTestId={dataTestId}
        background={Background.DARK}
        collapsible={false}
        header={
          <>
            {name && <h2 className='table-name'>{name}</h2>}
            {filterBar && (
              <div className='widget-filter-bar-container' ref={setFilterBarContainer} />
            )}
          </>
        }
        rightActions={
          <>
            {headerRightActions}
            {enableAdd && !readOnly && !showsEmptyContent && (
              <Button
                size={Size.LG}
                leadingElement={<MdAdd />}
                loading={addButtonLoading}
                disabled={readOnly || addButtonLoading || isLoading}
                onClick={() => {
                  if (customEditor) {
                    customEditor.onAddClick();
                  } else {
                    openAddModal();
                  }
                }}
              >
                {addButtonText}
              </Button>
            )}
          </>
        }
        footer={
          showFooter && (
            <div className='table-actions'>
              <div className='table-actions-left'>
                {pendingChangeCount > 0 && (
                  <Button
                    size={Size.LG}
                    appearance={Appearance.EMPTY}
                    design={Intent.ERROR}
                    leadingElement={<MdOutlineReplay />}
                    onClick={() => {
                      clearChanges();
                      onDiscard?.();
                    }}
                  >
                    Revert All Changes
                  </Button>
                )}
              </div>
              <div className='table-actions-right'>
                {extraFooterActions}
                {pendingChangeCount > 0 && (
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    design={Intent.WARNING}
                    leadingElement={<RiAlertFill />}
                    onClick={() => setShowPendingChanges(prev => !prev)}
                  >
                    {seePendingChangesCopy}
                  </Button>
                )}
                <Button
                  size={Size.LG}
                  leadingElement={<MdSave />}
                  onClick={async () => {
                    setIsSaving(true);
                    try {
                      await onSaveModal({
                        modifiedData: Object.values(modifiedRows),
                        addedData: Object.values(addedRows),
                        deletedData: Object.values(deletedRows),
                        cleanup: (options?: { showSuccessDialog?: boolean }) => {
                          clearChanges();
                          if (options?.showSuccessDialog !== false) {
                            setShowSuccessDialog(true);
                          }
                        }
                      });
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  disabled={pendingChangeCount === 0}
                  loading={isSaving}
                >
                  {saveChangesCopy}
                </Button>
              </div>
            </div>
          )
        }
      >
        <div className='outer-table-container'>
          <BPMTable
            autoHeight={autoHeight}
            isLoading={isLoading}
            data={tableData}
            filterBar={filterBar}
            // Not `className` -- see BPMTable's storageKeySuffix doc.
            storageKeySuffix={className}
            externalFilterBarContainer={filterBar ? filterBarContainer : undefined}
            defaultAdvancedFilter={defaultAdvancedFilter}
            dynamicTokens={dynamicTokens}
            density={density}
            rowHeight={rowHeight}
            // Callers that still pass a fixed rowHeight keep the old 25px header.
            headerHeight={rowHeight == null ? undefined : ROW_HEIGHT}
            headers={tableHeaders}
            minColumnWidth={20}
            noRowsRenderer={emptyContent != null ? renderEmptyContent : NoRowsRenderer}
            rowHeightGetter={rowHeightGetter}
            rowHoverClass='hovered'
            pagination={pagination}
          />
        </div>
        <PendingChangesPanel
          headers={headers.filter(h => !h.hideInPendingChanges)}
          originalData={tableDataMap as unknown as Record<string, RowType>}
          getRowId={row => String(row.rowKey)}
          pendingChanges={{
            editedRows: Object.entries(modifiedRows).map(([rowKey, row]) => ({
              ...row,
              rowKey
            })),
            newRows: Object.values(addedRows) as RowType[],
            deletedRows: Object.values(deletedRows) as RowType[]
          }}
          showPendingChanges={showPendingChanges}
          setShowPendingChanges={setShowPendingChanges}
        />
        {!customEditor && editingData && (
          <ModalEditor<T>
            name={name}
            modalTitle={modalTitle}
            headers={headers}
            checkIsValid={checkIsValid}
            checkCanDelete={checkCanDelete}
            enableDelete={enableDelete}
            onHide={() => {
              setEditingData(null);
              setEditingType(null);
            }}
            isAddingNewRow={editingType === 'adding'}
            isPendingAdd={editingData.rowKey in addedRows}
            onSaveModal={(newData: T) => {
              if (saveOnEdit) {
                commitSingle(editingType === 'adding' ? { added: newData } : { modified: newData });
                return;
              }
              if (editingType === 'adding') {
                // rowKey is a uuid generated when the modal opened — the same key is reused on
                // re-edit, so inserting and updating an added row are the same operation.
                setAddedRows({
                  ...addedRows,
                  [editingData.rowKey]: newData
                });
              } else if (editingType === 'editing') {
                setModifiedRows({
                  ...modifiedRows,
                  [editingData.rowKey]: newData
                });
              }
            }}
            onDelete={() => {
              if (saveOnEdit) {
                commitSingle({ deleted: editingData as T });
                return;
              }
              if (editingData.rowKey in addedRows) {
                setAddedRows(prev => {
                  const next = { ...prev };
                  delete next[editingData.rowKey];
                  return next;
                });
                return;
              }
              setDeletedRows({
                ...deletedRows,
                [editingData.rowKey]: editingData as T
              });
              setModifiedRows(prev => {
                const next = { ...prev };
                delete next[editingData.rowKey];
                return next;
              });
            }}
            rowData={editingData}
            selectorOptions={selectorOptions}
            footerText={footerText}
            footerMetadata={footerMetadata}
            customFooterButtons={customModalFooterButtons}
            checkShowFooter={checkShowModalFooter}
            deleteButtonText={deleteButtonText}
            saveButtonText={saveButtonText}
          />
        )}
      </WidgetContainer>
      <AlertDialog
        show={showSuccessDialog}
        onHide={() => setShowSuccessDialog(false)}
        intent={Intent.SUCCESS}
        title='Changes Saved'
        body='Your changes have been saved successfully.'
        passthroughProps={{ 'data-testid': 'success-dialog' }}
      />
    </>
  );
};
